import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { TranslateLoader, TranslateService } from '@ngx-translate/core';

import { DEFAULT_CURRENCY, DEFAULT_LANG } from '~constants';
import { ApiTranslateLoader } from '~shared/i18n/api-translate.loader';
import { readCache } from '~shared/i18n/i18n-cache';

/** Clé localStorage de la langue choisie par l'utilisateur. */
const LOCALE_STORAGE_KEY = 'locale';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private translate = inject(TranslateService);
  // Le loader est le singleton fourni à TranslateModule ; on l'atteint pour
  // piloter la revalidation de fond. Injecté ici (après construction de
  // TranslateService), donc hors du cycle NG0200 du bootstrap.
  private loader = inject(TranslateLoader) as ApiTranslateLoader;

  availableLanguages: string[] = [DEFAULT_LANG, 'en'];

  currentLanguage: string;
  currentCurrency: string;

  init(): Promise<unknown> {
    // Un cache présent = catalogue déjà servi lors d'une visite précédente : le
    // boot s'affiche instantanément depuis localStorage et on revalide en tâche
    // de fond. Sans cache, le loader fait l'unique appel réseau initial — donc
    // pas de revalidation derrière (évite un double appel au tout premier
    // chargement). Capturé AVANT `use()`, qui peuplerait le cache.
    const hadCache = readCache() != null;

    this.availableLanguages = this.resolveAvailableLanguages();
    this.translate.addLangs(this.availableLanguages);
    this.translate.setDefaultLang(DEFAULT_LANG);

    const language = this.restoreLanguage();
    this.setCurrency(DEFAULT_CURRENCY);

    // Retourne la Promise afin que l'APP_INITIALIZER attende la fin du
    // chargement des traductions avant de rendre l'app.
    return firstValueFrom(this.translate.use(language)).then(() => {
      this.currentLanguage = language;
      this.declareLanguage(language);
      localStorage.setItem(LOCALE_STORAGE_KEY, language);
      // Le cache est désormais peuplé (1er chargement) : on recalcule la liste
      // des langues à partir des locales réellement renvoyées par l'API.
      this.availableLanguages = this.resolveAvailableLanguages();
      if (hadCache) {
        this.revalidateCatalog();
      }
    });
  }

  /**
   * Porte la langue courante sur `<html lang>` (RGAA 8.3).
   *
   * Le document declarait `en` en dur alors que la langue par defaut du produit
   * est le francais : un lecteur d'ecran prononcait donc TOUTE l'interface avec
   * une voix anglaise. La declaration doit suivre la langue affichee, y compris
   * apres un changement en cours de session — d'ou l'appel aux deux endroits ou
   * la langue est arretee.
   */
  private declareLanguage(language: string): void {
    document.documentElement.setAttribute('lang', language);
  }

  setLanguage(language: string) {
    // `use()` est purement en mémoire : toutes les locales sont déjà chargées
    // (cache/mémo du loader). Le changement de langue est instantané et ne
    // déclenche AUCUN appel réseau.
    this.currentLanguage = language;
    this.declareLanguage(language);
    this.translate.use(language);
    localStorage.setItem(LOCALE_STORAGE_KEY, language);
  }

  getLanguage(): string {
    return this.currentLanguage;
  }

  setCurrency(currency: string) {
    this.currentCurrency = currency;
  }

  getCurrency(): string {
    return this.currentCurrency;
  }

  /**
   * Langue au démarrage, par priorité : préférence stockée > langue navigateur
   * > défaut. Corrige le comportement précédent où `localStorage['locale']`
   * était écrit mais jamais relu (préférence utilisateur ignorée au refresh).
   */
  private restoreLanguage(): string {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && this.availableLanguages.includes(stored)) {
      return stored;
    }
    const browserLang = this.translate.getBrowserLang();
    if (browserLang && this.availableLanguages.includes(browserLang)) {
      return browserLang;
    }
    return DEFAULT_LANG;
  }

  /**
   * Langues disponibles = clés du catalogue en cache (toutes celles servies par
   * l'API), avec repli sur fr/en tant que le catalogue n'a pas encore été chargé.
   */
  private resolveAvailableLanguages(): string[] {
    const locales = Object.keys(readCache()?.locales ?? {});
    return locales.length ? locales : [DEFAULT_LANG, 'en'];
  }

  /**
   * Revalidation légère : un appel de fond compare `generated_at`. S'il a
   * changé, on rejoue le catalogue courant (reloadLang) pour surfacer les
   * traductions à jour sans avoir bloqué le rendu.
   */
  private revalidateCatalog(): void {
    this.loader.refreshPublicCatalogs().subscribe((changed) => {
      if (changed) {
        this.availableLanguages = this.resolveAvailableLanguages();
        this.translate.reloadLang(this.currentLanguage).subscribe();
      }
    });
  }
}
